// In all the hooks, as of the time of writing this comment, we are selectively passing dependencies so the lint ignores are warranted
// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { App as CapacitorApp } from "@capacitor/app";
import { isDefined } from "@clipboard-health/util-ts";
import { BaseChannel, SendbirdError } from "@sendbird/chat";
import {
  GroupChannel,
  GroupChannelHandler,
  GroupChannelListOrder,
  MyMemberStateFilter,
} from "@sendbird/chat/groupChannel";
import SendbirdProvider from "@sendbird/uikit-react/SendbirdProvider";
import { SdkStore } from "@sendbird/uikit-react/types/lib/types";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import {
  CHAT_CHANNEL_QUERY_LIMIT,
  FRIEND_CHAT_CUSTOM_TYPE,
  SENDBIRD_REQUEST_CANCELED_CODE,
} from "@src/appV2/Chat/constants";
import { ChatChannelCustomTypesFilter } from "@src/appV2/Chat/types";
import {
  DeploymentEnvironmentName,
  environmentConfig,
  isProductionNodeEnvironment,
} from "@src/appV2/environment";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlags } from "@src/appV2/FeatureFlags/useCbhFlags";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logError } from "@src/appV2/lib/analytics/log";
import { Worker } from "@src/appV2/Worker/api/types";
import { fetchAndSetUpcomingShifts, setChannels, updateChannel } from "@store/chat";
import { ReactElement, ReactNode, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { Dispatch } from "redux";
import { v4 as uuidv4 } from "uuid";

import { filterChannelsBySearchInput } from "../../appV2/Chat/ChatList/hooks/useGetChatChannels";
import { getCustomTypesFilterArray } from "../../appV2/Chat/ChatList/hooks/useGetChatChannels";
import { useSession } from "../store/helperHooks";

function useChatEffects(): void {
  const sendBirdState = useSendbirdStateContext();
  const sdk = sendBirdState?.stores?.sdkStore?.sdk;
  const dispatch = useDispatch();
  const ldFlags = useCbhFlags();
  const isSendBirdOptimisationEnabled =
    ldFlags[CbhFeatureFlag.SENDBIRD_CONCURRENT_CONNECTION_OPTIMISATION];

  useEffect(() => {
    if (!sdk || !sdk.groupChannel) {
      return undefined;
    }

    const uuid = uuidv4();

    const channelHandler = new GroupChannelHandler({
      onChannelChanged: (channel) => {
        updateChannel(dispatch, channel);
      },
      onMessageReceived: (_channel: BaseChannel, message) => {
        if (message.isAdminMessage()) {
          fetchAndSetUpcomingShifts(dispatch);
        }
      },
    });

    channelHandler.onUserReceivedInvitation = (channel: GroupChannel) => {
      updateChannel(dispatch, channel);
      fetchAndSetUpcomingShifts(dispatch);
    };

    sdk.groupChannel.addGroupChannelHandler(uuid, channelHandler);

    return () => sdk?.groupChannel.removeGroupChannelHandler?.(uuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdk]);

  useEffect(() => {
    fetchChannels({ sdk, dispatch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdk]);

  useEffect(() => {
    // Exit early and avoid adding listeners if the feature flag is turned off
    if (!isSendBirdOptimisationEnabled) {
      return () => null;
    }
    const handleTabChange = ({ isActive }) => {
      // sendbird connections states may be `OPEN` or `CLOSED` or `CONNECTING`
      // we only want to change state if the status is closed
      if (isActive && sdk?.connectionState === "CLOSED") {
        sdk?.setForegroundState?.();
        fetchChannels({ sdk, dispatch });
      }

      // We only want to change state if the status is not closed because if connecting
      // we don't want that connection to go through and disconnect immediately
      if (!isActive && sdk?.connectionState !== "CLOSED") {
        sdk?.setBackgroundState?.();
      }
    };

    // This is fired when user puts app in the background/foreground
    const subscription = CapacitorApp.addListener("appStateChange", handleTabChange);

    return () => {
      subscription.then((listenerHandle) => listenerHandle.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdk]);
}

export async function fetchChannels(params: {
  searchInput?: string;
  customTypesFilter?: ChatChannelCustomTypesFilter;
  sdk: SdkStore["sdk"];
  dispatch: Dispatch;
}) {
  const { searchInput, customTypesFilter, sdk, dispatch } = params;
  if (!isDefined(sdk?.groupChannel)) {
    setChannels(dispatch, []);
    return;
  }

  try {
    const customTypesFilterArray = getCustomTypesFilterArray(customTypesFilter);
    const query = sdk.groupChannel.createMyGroupChannelListQuery({
      includeEmpty: true,
      order: GroupChannelListOrder.LATEST_LAST_MESSAGE,
      limit: CHAT_CHANNEL_QUERY_LIMIT,
      myMemberStateFilter: MyMemberStateFilter.JOINED,
      ...(isDefined(customTypesFilterArray) && { customTypesFilter: customTypesFilterArray }),
      ...(isDefined(searchInput) && { channelNameContainsFilter: searchInput }),
    });

    const channels = await query.next();

    // Friends chat is deprecated
    const filteredChannels = channels.filter(
      (channel) => channel.customType !== FRIEND_CHAT_CUSTOM_TYPE
    );

    const channelsMatchingSearchInput = searchInput
      ? filterChannelsBySearchInput(filteredChannels, searchInput)
      : filteredChannels;

    setChannels(dispatch, channelsMatchingSearchInput);
  } catch (error) {
    if (error instanceof SendbirdError && error.code === SENDBIRD_REQUEST_CANCELED_CODE) {
      return;
    }

    logError(APP_V2_APP_EVENTS.GET_CHAT_CHANNELS_FAILURE, {
      error,
      metadata: { customTypesFilter, searchInput, message: error.message },
    });
    throw error;
  }
}

interface Props {
  children: ReactNode;
}

/**
 * This component adds effects to SendBird context provider.
 * Consider eliminating this and reorganizing the inner code to run `useSendBirdEffects`
 */
export function ChatContextEffects(props: Props): ReactElement {
  const { children } = props;
  useChatEffects();
  return <>{children}</>;
}

interface ChatProviderProps {
  userId: string;
  worker: Worker;
  children: ReactNode;
}

// Note: there is a lot of duplicated logic in this file and in useGetChatChannels.
// I'm leaving it as is for now since I'm primarily making changes for the hackathon friend chat feature.
// Fixing this will likely require getting rid of Redux and upgrading Sendbird to the latest SDK,
// which has a cleaner way to handle state changes (which are quite challenging, as evidenced by this code).
export function ChatProvider(props: ChatProviderProps): ReactElement {
  const { children, userId, worker } = props;
  const dispatch = useDispatch();
  const { sendBirdAccessToken } = useSession();
  const location = useLocation();
  const [connectToChatInLowerEnvironments, setConnectToChatInLowerEnvironments] = useState(false);

  useEffect(() => {
    fetchAndSetUpcomingShifts(dispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.pathname === "/home/myShifts") {
      fetchAndSetUpcomingShifts(dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  // We only want to set the appId in production or if the chat page is actively being visited, else this increases the usage of sendbird connections
  // Due to this change, the chat counter will not be updated in the app bar when the user has not visited the chat page
  useEffect(() => {
    if (
      location.pathname.includes("/chat") ||
      location.pathname.includes("/clipboard-guides-chat")
    ) {
      setConnectToChatInLowerEnvironments(true);
    }
  }, [location.pathname]);
  const appId =
    (isProductionNodeEnvironment() &&
      environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.PRODUCTION) ||
    connectToChatInLowerEnvironments
      ? environmentConfig.REACT_APP_SENDBIRD_APP_ID
      : "";
  return (
    <SendbirdProvider
      appId={appId}
      userId={userId}
      nickname={worker.name}
      accessToken={sendBirdAccessToken}
    >
      {children}
    </SendbirdProvider>
  );
}
